import { ofType } from 'redux-observable';
import { mergeMap, exhaustMap, withLatestFrom } from 'rxjs/operators';

import { ERROR_CONFLICT } from '@perpay-web/constants/httpStatusCodes';
import { analyticsIdentifyUser } from '@perpay-web/fintech/actions/analytics/userInfo';
import {
    replaceAuthTokens,
    partnerOnboardingRedirectExistingUser,
    refreshAccessToken,
} from '@perpay-web/fintech/actions/authentication';
import {
    fetchMarketplaceDashboardSummaryError,
    fetchMarketplaceDashboardSummarySuccess,
    fetchMarketplaceDashboardSummary,
} from '@perpay-web/fintech/actions/entities/marketplaceDashboardSummary';
import {
    marketplacePartnerOnboardingSignup as marketplacePartnerOnboardingSignupAction,
    marketplacePartnerOnboardingSignupSuccess,
    marketplacePartnerOnboardingSignupError,
    fetchMarketplaceSummaryForOnboarding as fetchMarketplaceSummaryForOnboardingActions,
    fetchMarketplaceSummaryForOnboardingError,
    fetchMarketplaceSummaryForOnboardingSuccess,
    submitMarketplaceCardApplicationError,
} from '@perpay-web/fintech/actions/entities/marketplacePartnerOnboarding';
import { submitMarketplaceCardApplicationSuccess as submitMarketplaceCardApplicationSuccessAction } from '@perpay-web/fintech/actions/entities/marketplacePartnerOnboarding';
import {
    analyticsSignupSuccess,
    analyticsOnboardingAddPhoneNumber,
} from '@perpay-web/fintech/actions/ui/signup';
import {
    APPLICATION_STEP,
    CLARITY_UNFREEZE_STEP,
    DENIED_STEP,
    IDV_FAILED_STEP,
} from '@perpay-web/fintech/components/screens/MarketplaceCardApplication/MarketplaceCardApplicationSteps';
import { SUCCESS_STEP } from '@perpay-web/fintech/components/screens/MarketplaceCardApplication/MarketplaceCardApplicationSteps';
import { marketplaceCardApplicationSetSteps } from '@perpay-web/fintech/components/screens/MarketplaceCardApplication/useMarketplaceCardApplicationContext';
import { SIGNUP_FLOW_PARTNER_ONBOARDING } from '@perpay-web/fintech/constants/signupFlowTypes';
import {
    partnerOnboardingExistingUserModalSetExistingUserFirstName,
    partnerOnboardingExistingUserModalSetIsModalOpen,
    partnerOnboardingExistingUserModalSetMatchMethod,
} from '@perpay-web/fintech/hooks/usePartnerOnboardingExistingUserModalContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { getUserInfo } from '@perpay-web/fintech/selectors/entities/userInfo';
import { authentication } from '@perpay-web/fintech/settings/singletons';
import { installSegmentMiddleware } from '@perpay-web/fintech/utils/analyticsUtils';
import { getPartnerNameFromCode } from '@perpay-web/fintech/utils/partnerOnboardingUtils';
import { dispatchObservable } from '@perpay-web/observable/dispatchObservable';
import { handleError } from '@perpay-web/observable/operators/handleError';
import { replace } from '@perpay-web/services/router';
import { getAnalyticsIdentity } from '@perpay-web/utils/tokenUtils';

export const marketplacePartnerOnboardingSignup = (action$, state$) =>
    action$.pipe(
        ofType(marketplacePartnerOnboardingSignupAction().type),
        exhaustMap((action) =>
            authentication
                .marketplacePartnerHostedSignup(action.payload)
                .then((results) =>
                    installSegmentMiddleware().then(() => results),
                ),
        ),
        withLatestFrom(state$),
        mergeMap(([results, state]) => {
            const { tokens: unusedTokens, ...rest } = results;

            const decodedTokens = authentication.getDecodedTokens();
            const identityAction = getAnalyticsIdentity(
                decodedTokens,
                getUserInfo(state),
            );
            const partnerCode = authentication.getPartnerOnboardedCode();
            const partnerName = getPartnerNameFromCode(partnerCode);
            const uuid = authentication.getUserUuid();
            replace(
                `${paths.marketplacePartnerOnboardingApplication.path}/${window.location.search}`,
            );
            return [
                analyticsIdentifyUser(identityAction),
                replaceAuthTokens(decodedTokens),
                marketplacePartnerOnboardingSignupSuccess(rest),
                analyticsSignupSuccess({
                    ...rest,
                    uuid,
                    signupFlow: SIGNUP_FLOW_PARTNER_ONBOARDING,
                    partner: partnerName,
                }),
                analyticsOnboardingAddPhoneNumber({
                    signupFlow: SIGNUP_FLOW_PARTNER_ONBOARDING,
                    partner: partnerName,
                }),
                marketplaceCardApplicationSetSteps(APPLICATION_STEP),
            ];
        }),
        handleError((error) => {
            const returnActions = [
                marketplacePartnerOnboardingSignupError(error),
            ];

            if (error.status === ERROR_CONFLICT) {
                const {
                    message,
                    name: firstName,
                    method,
                    mfaRequired,
                } = error.response;
                const isMfaRequired = mfaRequired ? mfaRequired[0] : false;

                returnActions.push(partnerOnboardingRedirectExistingUser());

                if (
                    (method && method === 'phone') ||
                    message[0].includes('Phone') ||
                    isMfaRequired
                ) {
                    replace(paths.login.path);
                    return returnActions;
                }
                returnActions.push(
                    partnerOnboardingExistingUserModalSetIsModalOpen(true),
                );
                if (firstName) {
                    returnActions.push(
                        partnerOnboardingExistingUserModalSetExistingUserFirstName(
                            firstName,
                        ),
                    );
                }
                if (method) {
                    returnActions.push(
                        partnerOnboardingExistingUserModalSetMatchMethod(
                            method,
                        ),
                    );
                }
            }
            return returnActions;
        }),
    );

export const fetchMarketplaceSummaryForOnboarding = (action$, state$) =>
    action$.pipe(
        ofType(fetchMarketplaceSummaryForOnboardingActions().type),
        exhaustMap(() =>
            dispatchObservable({
                action$,
                state$,
                initialDispatch: () => [fetchMarketplaceDashboardSummary()],
                waitFor: [fetchMarketplaceDashboardSummarySuccess().type],
                waitForDispatch: (state, results) => {
                    const returnActions = [
                        fetchMarketplaceSummaryForOnboardingSuccess({}),
                    ];
                    const { payload } = results[0];

                    if (
                        payload.availableSpendingLimit &&
                        !payload.isSpendingLimitEstimate
                    ) {
                        replace(paths.dashboard.path);
                    } else if (
                        !payload.ssnIsVerified &&
                        payload.ssnVerificationAttempts >= 1
                    ) {
                        returnActions.push(
                            marketplaceCardApplicationSetSteps(IDV_FAILED_STEP),
                        );
                    } else
                        returnActions.push(
                            marketplaceCardApplicationSetSteps(
                                APPLICATION_STEP,
                            ),
                        );
                    return returnActions;
                },
                errors: [fetchMarketplaceDashboardSummaryError().type],
                errorDispatch: (result) => {
                    const error = result.payload;

                    return [fetchMarketplaceSummaryForOnboardingError(error)];
                },
            }),
        ),
    );

export const submitMarketplaceCardApplicationSuccess = (action$) =>
    action$.pipe(
        ofType(submitMarketplaceCardApplicationSuccessAction().type),
        mergeMap((action) => {
            /** @type {import('@reduxjs/toolkit').Action[]} */
            const returnActions = [refreshAccessToken()];
            if (action.payload.result === 'success') {
                returnActions.push(
                    marketplaceCardApplicationSetSteps(SUCCESS_STEP),
                );
            }
            if (action.payload.result === 'failed') {
                if (action.payload.clarityFrozen) {
                    return [
                        marketplaceCardApplicationSetSteps(
                            CLARITY_UNFREEZE_STEP,
                        ),
                    ];
                }
                if (action.payload.idvFailed) {
                    if (action.payload.ssnInvalid) {
                        returnActions.push(
                            submitMarketplaceCardApplicationError(
                                'SSN is invalid',
                            ),
                        );
                    } else {
                        return [
                            marketplaceCardApplicationSetSteps(IDV_FAILED_STEP),
                        ];
                    }
                }
                returnActions.push(
                    marketplaceCardApplicationSetSteps(DENIED_STEP),
                );
            }
            return returnActions;
        }),
    );
